import React, { useEffect, useMemo, useRef } from 'react';
import moment, { Moment } from 'moment';

import DatePickerComponent, {
  DatePickerComponentProps,
} from 'app/components/DatePickerComponent';
import { useSelector } from 'react-redux';
import { selectInfoUser } from 'app/pages/App/slice/selectors';

export interface Props extends DatePickerComponentProps {
  onDefaultValue?: (value: Moment) => void;
}

const TicketDateSelectable: React.FC<Props> = props => {
  const { onDefaultValue, ...restProps } = props;
  const defaultValue = useRef<Moment | undefined>(undefined);
  const isInit = useRef(true);
  const userInfo = useSelector(selectInfoUser);
  const minDateMoment = useMemo(() => {
    const possibleValues = [
      moment().startOf('day'),
      moment(
        userInfo?.businessPartner?.startContact?.date,
        'YYYY/MM/DD',
      ).startOf('day'),
    ];
    return possibleValues.reduce((max, item) => {
      if (max.isBefore(item)) return item;
      return max;
    });
  }, [userInfo?.businessPartner?.startContact?.date]);

  const maxDateMoment = useMemo(() => {
    const possibleValues = [
      moment(userInfo?.businessPartner?.endContact?.date, 'YYYY/MM/DD').startOf(
        'day',
      ),
    ];
    return possibleValues.reduce((min, item) => {
      if (min.isAfter(item)) return item;
      return min;
    });
  }, [userInfo?.businessPartner?.endContact?.date]);

  const disabledDate = (current: Moment) => {
    const isOutOfRange =
      current.isBefore(minDateMoment, 'day') ||
      current.isAfter(maxDateMoment, 'day');

    return isOutOfRange;
  };

  const defaultPickerValue = useMemo(() => {
    if (defaultValue.current) return defaultValue.current;
  }, []);

  useEffect(() => {
    if (isInit.current && defaultValue.current && onDefaultValue) {
      defaultValue.current && onDefaultValue(defaultValue.current);
      isInit.current = false;
    }
  }, [onDefaultValue]);

  return (
    <DatePickerComponent
      allowClear={false}
      defaultValue={defaultValue.current}
      defaultPickerValue={defaultPickerValue}
      inputReadOnly
      disabledDate={disabledDate}
      format="DD/MM/YYYY"
      {...restProps}
    />
  );
};

export default React.memo(TicketDateSelectable);
